import { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { BadgeCheck, Loader2 } from "lucide-react";
import { api } from "@/lib/api";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [data, setData] = useState(null);
  const tries = useRef(0);

  useEffect(() => {
    if (!sessionId) return;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        setData(data);
        if (data.payment_status !== "paid" && tries.current < 15) {
          tries.current += 1;
          setTimeout(poll, 2000);
        }
      } catch {
        if (tries.current < 15) {
          tries.current += 1;
          setTimeout(poll, 2000);
        }
      }
    };
    poll();
  }, [sessionId]);

  const paid = data?.payment_status === "paid";

  return (
    <main className="min-h-screen flex items-center justify-center px-4 bg-clay-bg">
      <motion.div
        data-testid="payment-success-card"
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        className="max-w-md w-full border border-clay-border bg-clay-card p-10 text-center"
      >
        {paid ? (
          <>
            <BadgeCheck className="w-16 h-16 text-clay-gold mx-auto mb-6" />
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-clay-orange mb-3">Payment Confirmed</p>
            <h1 className="font-display font-black uppercase text-4xl tracking-tight mb-4">
              {data?.kind === "sponsorship"
                ? `Welcome Aboard${data?.business_name ? `, ${data.business_name}` : ""}`
                : data?.kind === "donation"
                ? `You're a Legend${data?.member_name && data.member_name !== "Anonymous donor" ? `, ${data.member_name.split(" ")[0]}` : ""}`
                : data?.kind === "program"
                ? "Program Unlocked"
                : `Welcome to the Family${data?.member_name ? `, ${data.member_name.split(" ")[0]}` : ""}`}
            </h1>
            <p className="text-clay-muted text-sm mb-8">
              {data?.kind === "sponsorship"
                ? `Your ${data?.package_label || "sponsorship"} is confirmed. The club will be in touch about your logo, signage and naming rights — a confirmation email is on its way.`
                : data?.kind === "donation"
                ? `Your ${data?.amount ? `$${data.amount.toFixed(2)} ` : ""}donation is confirmed. You're keeping grassroots speedway alive in Darlington — see you at the track.`
                : data?.kind === "program"
                ? "Your race day program is ready — open it below, and the same link has been emailed to you so you can print it anytime."
                : "Your 2026/27 season membership is active. See you at the track — dirt track racing at its best."}
            </p>
            {data?.kind === "program" && data?.program_ref && (
              <a
                data-testid="success-program-link"
                href={`/program/${data.program_ref}?key=${sessionId}`}
                className="inline-block bg-clay-orange hover:bg-clay-gold text-clay-bg font-display font-bold uppercase tracking-wider px-8 py-3 transition-colors duration-300 mb-2"
              >
                Open Your Program
              </a>
            )}
          </>
        ) : (
          <>
            <Loader2 className="w-12 h-12 text-clay-orange mx-auto mb-6 animate-spin" />
            <h1 className="font-display font-bold uppercase text-3xl tracking-tight mb-3">Confirming Payment…</h1>
            <p className="text-clay-muted text-sm">Hold tight while Stripe confirms your membership payment.</p>
          </>
        )}
        <Link
          data-testid="success-home-link"
          to="/"
          className="inline-block mt-6 border border-clay-border hover:border-clay-orange font-display font-bold uppercase tracking-wider px-8 py-3 transition-colors duration-300"
        >
          Back to the Track
        </Link>
      </motion.div>
    </main>
  );
}
